import React from 'react';
import { motion } from 'framer-motion';
import { Award, Users } from 'lucide-react';

const About: React.FC = () => {
  const stats = [
    { icon: Award, label: 'Years Experience', value: '15+' },
    { icon: Users, label: 'Team Size Led', value: '20+' },
  ];

  const competencies = [
    {
      category: 'Technical Leadership',
      items: [
        'Team Management & Mentoring',
        'Solution Architecture',
        'System Design',
        'Performance Optimization',
        'Code Review & Quality Assurance',
        'Production Support & Monitoring'
      ]
    },
    {
      category: 'Methodologies',
      items: [
        'Agile/Scrum Development',
        'CI/CD Implementation',
        'REST API Design',
        'Microservices Architecture'
      ]
    }
  ];

  return (
    <section id="about" className="section-padding bg-gray-50 dark:bg-dark-800">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-6">
            About <span className="gradient-text">Me</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            A passionate technology leader dedicated to building scalable, high-performance 
            enterprise solutions that drive business growth and innovation.
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 gap-8 mb-16 max-w-2xl mx-auto"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full mb-4">
                <stat.icon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                {stat.value}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Description */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
              Executive Summary
            </h3>
            <div className="space-y-4 text-gray-600 dark:text-gray-400">
              <p>
                Senior technology leader with 15+ years of progressive experience architecting 
                and delivering enterprise-scale solutions. Proven track record of leading 
                high-performing teams, driving digital transformation initiatives, and optimizing 
                system performance across diverse industries.
              </p>
              <p>
                Expert in Java ecosystem, cloud technologies, and agile methodologies with a 
                strong focus on scalable architecture and operational excellence. Successfully 
                managed teams ranging from 4-20+ professionals across multiple organizations.
              </p>
              <p>
                Achieved significant performance improvements (40-50%) through optimization 
                and modernization, while maintaining zero downtime deployments with comprehensive 
                monitoring frameworks.
              </p>
            </div>
          </motion.div>

          {/* Right Column - Competencies */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            {competencies.map((competency, index) => (
              <motion.div
                key={competency.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="card p-6"
              >
                <h4 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  {competency.category}
                </h4>
                <ul className="space-y-2">
                  {competency.items.map((item, itemIndex) => (
                    <motion.li
                      key={item}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: itemIndex * 0.1 }}
                      viewport={{ once: true }}
                      className="flex items-center text-gray-600 dark:text-gray-400"
                    >
                      <div className="w-2 h-2 bg-primary-500 rounded-full mr-3 flex-shrink-0"></div>
                      {item}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Key Achievements */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-16"
        >
          <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 text-center mb-8">
            Key Achievements
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              'Successfully managed teams ranging from 4-20+ professionals',
              'Improved application performance by 40-50% through optimization',
              'Achieved seamless production deployments with comprehensive monitoring',
              'Established strong relationships with C-level executives'
            ].map((achievement, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card p-6 text-center"
              >
                <p className="text-gray-600 dark:text-gray-400">
                  {achievement}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
